import {
  AlertTimeoutThreshold,
  CommonConfiguration,
  CommonSensorStatus,
  CommonSubSensorStatus,
  HasSubSensorStatuses,
  HostConfiguration,
  PortConfiguration,
  PositionSubSensor,
  SensorTypeEnum,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from './common.types';
import { CommonRadarConfiguration } from './radar.types';

export interface SpexerRadar extends CommonRadarConfiguration, PositionSubSensor {
  radarId: number;
  type: SubSensorTypeEnum.SPEXER_RADAR;
}

export enum SpexerVersionEnum {
  SPEXER_500 = 'SPEXER_500',
  SPEXER_2000 = 'SPEXER_2000',
}

export interface SpexerSensorConfiguration
  extends CommonConfiguration, HostConfiguration, PortConfiguration, AlertTimeoutThreshold {
  type: SensorTypeEnum.SPEXER;
  version: SpexerVersionEnum;
  radars: SpexerRadar[];
}

// 0 : ACTIVE
// 1 : STANDBY
// 2 : UNKNOWN
type OperationMode = 0 | 1 | 2;

// 0 : LAND
// 1 : SEA
// 2 : AIR
type Terrain = 0 | 1 | 2;

// 0 : FIXED
// 1 : AGILE
type TxMode = 0 | 1;

// 0 : NO_CLASSIFICATION
// 1 : STEP_1
// 2 : STEP_2
// 3 : STEP_3
type Classification = 0 | 1 | 2 | 3;

// 0 : IGNORE
// 1 : USE
type ClusterAlgorithm = 0 | 1;

export type Spexerv500Parameters = {
  boreSightAzimuth: number;
  terrain: Terrain;
  sectorWidth: number;
  sectorStartDistance: number;
  sectorEndDistance: number;
  manualThreshold: number;
  prfStaggering: 'On' | 'Off';
  txChannel: number;
  txPower: number;
  txMode: TxMode;
  classification: Classification;
  minSpeed: number;
  maxSpeed: number;
  clusterAlgorithm: ClusterAlgorithm;
  clusterAlgorithmParameters: {
    fi: number;
    r: number;
    rs: number;
  };
};

export interface SpexerRadarStatus extends CommonSubSensorStatus {
  type: SubSensorTypeEnum.SPEXER_RADAR;
  operationMode: OperationMode | null;
  scanTime: number | null;
  parameters: Spexerv500Parameters | null;
  specificState: SpecificStateEnum.EMITTING | null;
}

interface HighLevelSpexerPdState {
  operatingState: OperatingStateEnum;
  transmitterState: TransmitterStateEnum;
  preset: number;
}

interface SensorPDPutRequestEmconLockedChannels {
  _0: number[];
  _1: number[];
  _2: number[];
  _3: number[];
  _4: number[];
  _5: number[];
  _6: number[];
  _7: number[];
  _8: number[];
  _9: number[];
  _10: number[];
  _11: number[];
  _12: number[];
  _13: number[];
  _14: number[];
  _15: number[];
  _16: number[];
  _17: number[];
  _18: number[];
  _19: number[];
  _20: number[];
  _21: number[];
  _22: number[];
  _23: number[];
  _24: number[];
  _26: number[];
  _27: number[];
  _28: number[];
  _29: number[];
  _30: number[];
  _31: number[];
}

interface SensorTag {
  type: string;
  number: number;
}

interface SensorPDPutRequestSurveillanceAreasInner {
  origin: SensorTag;
  sector: SensorPDPutRequestSurveillanceAreasInnerSector;
}

interface SensorPDPutRequestSurveillanceAreasInnerSectorBeams {
  azimuth: number;
  elevation: number;
}

enum CfarEnum {
  NOCFARSELECTED = 'NoCfarSelected',
  CFARTYPE1 = 'CfarType1',
  CFARTYPE2 = 'CfarType2',
  CFARTYPE3 = 'CfarType3',
  CFARTYPE4 = 'CfarType4',
  CFARTYPE5 = 'CfarType5',
}

enum FrequencyAgilityEnum {
  SINGLECHANNEL = 'SingleChannel',
  PSEUDORANDOMFREQUENCY = 'PseudoRandomFrequency',
}

enum RangeGateEnum {
  METER10 = 'Meter10',
  METER20 = 'Meter20',
  METER40 = 'Meter40',
  METER80 = 'Meter80',
}

enum TerrainEnum {
  GROUND = 'Ground',
  SEA = 'Sea',
  AIR = 'Air',
}

enum EtargetSpeedEnum {
  NOSPEEDSELECTED = 'NoSpeedSelected',
  SLOW = 'Slow',
  MEDIUM = 'Medium',
  FAST = 'Fast',
}

enum TxPowerEnum {
  PERC100_0DB = 'Perc100_0dB',
  PERC50_3DB = 'Perc50_3dB',
  PERC25_6DB = 'Perc25_6dB',
  PERC12_5_9DB = 'Perc12_5_9dB',
  TXBOOST = 'TxBoost',
}

enum DopplerAudioAntennaSelectionEnum {
  _1 = 'ANU_1',
  _2 = 'ANU_2',
  _3 = 'ANU_3',
}

enum BinaryIntegrationEnum {
  TWOOUTOFFOUR = 'TwoOutOfFour',
  TWOOUTOFTHREE = 'TwoOutOfThree',
}

enum BurstEnum {
  _64 = 'Pulses_64',
  _128 = 'Pulses_128',
  _256 = 'Pulses_256',
}

interface SensorPDPutRequestSurveillanceAreasInnerSector {
  name: SensorTag;
  azimuth: number;
  sectorWidth: number;
  sectorEnd: number;
  sectorStart: number;
  elevation: number;
  cfar: CfarEnum;
  audioGate: number;
  txChannel: number;
  frequencyAgility: FrequencyAgilityEnum;
  manualThreshold: number;
  minSpeed: number;
  prfStaggering: boolean;
  rangeGate: RangeGateEnum;
  terrain: TerrainEnum;
  etargetSpeed: EtargetSpeedEnum;
  trackIdentifier: number;
  txPower: TxPowerEnum;
  dopplerAudioAntennaSelection: DopplerAudioAntennaSelectionEnum;
  modeAntennaSelection: DopplerAudioAntennaSelectionEnum;
  binaryIntegration: BinaryIntegrationEnum;
  burst: BurstEnum;
  dopplerAudio: boolean;
  beams: SensorPDPutRequestSurveillanceAreasInnerSectorBeams;
}

interface SensorPDPutRequestPositionBehaviorFix {
  latitude: number;
  longitude: number;
  altitude: number;
}

interface SensorPDPutRequestPositionBehavior {
  fix: SensorPDPutRequestPositionBehaviorFix;
  useFromInu: boolean;
}

interface SensorPDPutRequestAttitudeBehaviorFix {
  boresight: number;
  tilt: number;
}

interface SensorPDPutRequestAttitudeBehaviorPedestal {
  northCorrection: number;
  parkPosition: boolean;
}

interface SensorPDPutRequestAttitudeBehavior {
  fix: SensorPDPutRequestAttitudeBehaviorFix;
  useFromInu: boolean;
  pedestal: SensorPDPutRequestAttitudeBehaviorPedestal;
}

interface SensorPDPutRequestInuValues {
  latitude: number;
  longitude: number;
  altitude: number;
  heading: number;
  pitch: number;
  roll: number;
  vnorth: number;
  veast: number;
  vdown: number;
}

export enum OperatingStateEnum {
  STANDBY = 'Standby',
  ON = 'On',
  IBIT = 'Ibit',
  MAINTENANCE = 'Maintenance',
  UNKNOWN = 'Unknown',
}

export enum TransmitterStateEnum {
  ON = 'On',
  OFF = 'Off',
}

interface FullSpexerPdState {
  operatingState: OperatingStateEnum;
  transmitterState: TransmitterStateEnum;
  dualBeam: boolean;
  inuValues: SensorPDPutRequestInuValues;
  attitudeBehavior: SensorPDPutRequestAttitudeBehavior;
  positionBehavior: SensorPDPutRequestPositionBehavior;
  surveillanceAreas: SensorPDPutRequestSurveillanceAreasInner[];
  emconLockedChannels: SensorPDPutRequestEmconLockedChannels;
}

export interface SpexerSensorStatus extends CommonSensorStatus, HasSubSensorStatuses<SpexerRadarStatus> {
  type: SensorTypeEnum.SPEXER;
  highLevelSpexerPdState: HighLevelSpexerPdState | null;
  fullSpexerPdState: FullSpexerPdState | null;
}
